import { Injectable, NgZone, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormularioDialogService } from './formulario-dialog.service';
import { BehaviorSubject, Subject, fromEvent, merge, throttleTime } from 'rxjs';
import { IControlInactividad } from 'modelos/src';
import { HelperService } from './helper.service';
import { EstadisticasUsoService } from './http/estadisticas-uso.service';
import { ActividadUsuarioService } from './actividad-usuario.service';
import { LoginService } from '../../modulos/login/login.service';
import {
  ConfirmarActividadDialogComponent,
  ConfirmarActividadData,
} from '../confirmar-actividad-dialog/confirmar-actividad-dialog.component';

/**
 * Control de atención del usuario (mismo patrón que el IdleService de
 * seguridad-admin-web). Tras un tiempo configurable sin interacción
 * (mouse / teclado / touch) pasa a idle y abre un popup que el usuario debe
 * confirmar dentro de otro tiempo configurable. Ambos tiempos salen de la
 * configuración del cliente (cliente.config.controlInactividad); si el
 * cliente no tiene configurados los dos, el control no corre.
 * Solo aplica a permisos con rol Operador (ver HelperService.esRolOperador).
 */
@Injectable({
  providedIn: 'root',
})
export class InactividadUsuarioService {
  private formularios = inject(FormularioDialogService);
  private helper = inject(HelperService);
  private ngZone = inject(NgZone);
  private estadisticasUso = inject(EstadisticasUsoService);
  private actividad = inject(ActividadUsuarioService);

  /** true mientras el operador está en descanso: pausa la detección de
   *  inactividad (durante el descanso puede alejarse sin escalar). */
  private enDescanso = false;

  constructor() {
    // Pausa/rearma según el estado de descanso del control de actividad.
    this.actividad.enDescanso$.subscribe((enDescanso) => {
      this.enDescanso = enDescanso;
      if (enDescanso) {
        clearTimeout(this.timerInactividad!);
      } else {
        this.onInteraction();
      }
    });
  }

  /** true mientras el usuario está inactivo (popup abierto) */
  public idle$ = new BehaviorSubject(false);
  /** Emite cuando el usuario confirma actividad después de estar idle */
  public wake$ = new BehaviorSubject(true);
  /** Emite en cada vencimiento del popup sin confirmación. */
  public escalado$ = new Subject<void>();

  public isIdle = false;
  private activo = false;
  private eventosSuscriptos = false;
  /** true si el popup actual venció sin confirmación (intervalo abierto) */
  private huboEscalado = false;
  private timerInactividad?: NodeJS.Timeout;
  private dialogRef?: MatDialogRef<ConfirmarActividadDialogComponent, boolean>;

  /** Idempotente. Llamar al iniciar sesión y en cada cambio de permiso:
   *  re-evalúa el rol y arma o desarma la detección según corresponda. */
  public iniciarServicio(): void {
    this.activo =
      !!LoginService.getAccessToken() &&
      this.helper.esRolOperador() &&
      !!this.getConfig();
    if (!this.activo) {
      this.detener();
      return;
    }
    if (!this.eventosSuscriptos) {
      this.eventosSuscriptos = true;
      // Fuera de la zona de Angular: mousemove/wheel ya corren fuera por
      // zone-flags.ts y acá no hace falta change detection para rearmar el
      // timer; así ninguna tecla/movimiento dispara un ciclo de CD extra.
      // El throttle evita clearTimeout + lectura de config a ~100 Hz.
      this.ngZone.runOutsideAngular(() => {
        merge(
          fromEvent(document, 'mousemove'),
          fromEvent(document, 'touchstart'),
          fromEvent(document, 'keydown'),
          fromEvent(document, 'wheel'),
        )
          .pipe(throttleTime(1000))
          .subscribe(() => this.onInteraction());
      });
    }
    this.onInteraction();
  }

  /** Config del cliente propio. Devuelve null si el cliente no tiene
   *  configurados ambos tiempos — sin config completa el control no corre.
   *  Se lee en cada uso para tomar cambios de configuración sin recargar. */
  private getConfig(): Required<IControlInactividad> | null {
    const cliente =
      LoginService.getClientePropio() || LoginService.getCliente();
    const config = cliente?.config?.controlInactividad;
    if (!config?.limTiempoInactividad || !config?.limTiempoConfirm) {
      return null;
    }
    return {
      limTiempoInactividad: config.limTiempoInactividad,
      limTiempoConfirm: config.limTiempoConfirm,
    };
  }

  private onInteraction(): void {
    // En idle (popup abierto) la única interacción válida es el botón de
    // confirmar; el timer se rearma recién en onWake(). En descanso el control
    // queda pausado y se rearma al terminar el descanso.
    if (!this.activo || this.isIdle || this.enDescanso) return;

    // El logout por navegación SPA (bienvenida / redirect por 401) no
    // destruye el servicio: sin token no hay sesión que controlar
    if (!LoginService.getAccessToken()) {
      this.detener();
      return;
    }

    clearTimeout(this.timerInactividad!);
    const config = this.getConfig();
    // Le sacaron la config al cliente con el servicio armado
    if (!config) {
      this.detener();
      return;
    }
    const ms = config.limTiempoInactividad * 60_000;
    this.timerInactividad = setTimeout(() => this.onIdle(), ms);
  }

  private onIdle(): void {
    if (this.isIdle || this.enDescanso) return;
    // La sesión pudo expirar (401 → /login) con el servicio todavía armado
    if (!LoginService.getAccessToken()) {
      this.detener();
      return;
    }

    // La config pudo desaparecer entre que se armó el timer y venció
    const config = this.getConfig();
    if (!config) {
      this.detener();
      return;
    }

    this.isIdle = true;
    // El timer se armó desde un evento fuera de la zona → re-entrar para que
    // el diálogo y su countdown rendericen (sin esto el popup no aparece
    // hasta el próximo ciclo de CD que dispare otra cosa)
    this.ngZone.run(() => {
      this.idle$.next(true);
      const data: ConfirmarActividadData = {
        segundosConfirmacion: config.limTiempoConfirm,
        minutosInactividad: config.limTiempoInactividad,
      };
      this.dialogRef = this.formularios.abrir<
        ConfirmarActividadDialogComponent,
        ConfirmarActividadData,
        boolean
      >(ConfirmarActividadDialogComponent, {
        tamano: 'chico',
        data,
      });
      this.dialogRef.componentInstance.escalado.subscribe(() => {
        // Venció el countdown sin confirmación: el usuario pasó a inactivo.
        // Se abre su intervalo en estadísticas de uso (fire-and-forget).
        this.huboEscalado = true;
        this.estadisticasUso.registrarInactividad().catch(() => {});
        this.escalado$.next();
      });
      this.dialogRef.afterClosed().subscribe(() => {
        this.dialogRef = undefined;
        // Confirmó después del escalado: estaba inactivo y volvió → cierra
        // su intervalo abierto
        if (this.huboEscalado) {
          this.huboEscalado = false;
          this.estadisticasUso.finalizarInactividad().catch(() => {});
        }
        this.onWake();
      });
    });
  }

  private onWake(): void {
    this.isIdle = false;
    this.idle$.next(false);
    this.wake$.next(true);
    this.onInteraction();
  }

  /** Desarma el control: timer y popup incluidos. Llamar cuando el usuario
   *  sale del shell de la app (destroy de NavigationComponent, ej: flecha
   *  atrás del navegador hacia el login) — el token queda en sessionStorage
   *  así que "hay token" no alcanza para saber que sigue adentro. */
  public detener(): void {
    this.activo = false;
    clearTimeout(this.timerInactividad!);
    // Si el popup escaló, el intervalo queda abierto: irse sin confirmar no
    // es "volvió a estar activo" (mismo criterio que cerrar el navegador).
    this.huboEscalado = false;
    // close() dispara afterClosed → onWake, que resetea isIdle/idle$
    this.dialogRef?.close();
  }
}
